import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface FolderProgress {
  path: string;
  progress: number;
}

interface ScanningState {
  isScanning: boolean;
  progress: number;
  isDropdownOpen: boolean;
  folders: FolderProgress[];
}

const initialState: ScanningState = {
  isScanning: false,
  progress: 0,
  isDropdownOpen: false,
  folders: [],
};

// Helper function to calculate overall progress from folders
const calculateOverallProgress = (folders: FolderProgress[]): number => {
  if (folders.length === 0) return 0;

  // Calculate the average progress across all folders
  const totalProgress = folders.reduce(
    (sum, folder) => sum + folder.progress,
    0,
  );
  return totalProgress / folders.length;
};

export const scanningSlice = createSlice({
  name: 'scanningState',
  initialState,
  reducers: {
    setScanning: (state, action: PayloadAction<boolean>) => {
      state.isScanning = action.payload;
      if (!action.payload) {
        state.progress = 0;
        state.folders = [];
      }
    },
    setScanProgress: (state, action: PayloadAction<number>) => {
      state.progress = action.payload;
    },
    setDropdownOpen: (state, action: PayloadAction<boolean>) => {
      state.isDropdownOpen = action.payload;
    },
    updateFolderProgress: (state, action: PayloadAction<FolderProgress>) => {
      const { path, progress } = action.payload;
      const existingFolder = state.folders.find((f) => f.path === path);
      if (existingFolder) {
        existingFolder.progress = progress;
      } else {
        state.folders.push({ path, progress });
      }

      // Update the overall progress whenever a folder's progress changes
      state.progress = calculateOverallProgress(state.folders);
    },
    removeFolder: (state, action: PayloadAction<string>) => {
      state.folders = state.folders.filter((f) => f.path !== action.payload);

      // Recalculate overall progress when a folder is removed
      state.progress = calculateOverallProgress(state.folders);
    },
  },
});

export const {
  setScanning,
  setScanProgress,
  setDropdownOpen,
  updateFolderProgress,
  removeFolder,
} = scanningSlice.actions;
export default scanningSlice.reducer;
